import {
	DataTypeProvider,
	DataTypeProviderProps,
	IntegratedSorting,
	Sorting,
	SortingState,
	TableColumnWidthInfo,
} from '@devexpress/dx-react-grid';
import {
	DragDropProvider,
	Grid,
	Table,
	TableColumnReordering,
	TableColumnResizing,
	TableFixedColumns,
	TableHeaderRow,
	VirtualTable,
} from '@devexpress/dx-react-grid-bootstrap4';
import { IUnitServiceHistoryDto, ServiceDocumentType } from 'api/api';
import { Guid } from 'guid-typescript';
import { Button } from 'primereact/components/button/Button';
import React, { FC, memo, useCallback, useState } from 'react';
import { unitServiceHistoryClient } from 'state/ducks/api-clients';
import { SortingGeneric, TableColumnWidthInfoGeneric } from 'state/ducks/history-table-settings/types';
import { setOrder, setSize, setSorting } from 'state/ducks/service-history-table-settings/operations';
import { ServiceHistoryColumnNames, ServiceHistoryDtoKeys } from 'state/ducks/service-history-table-settings/types';
import { getUnitServiceHistories } from 'state/ducks/unit-service-history/operations';
import { localized } from 'state/i18n';
import { useAppDispatch, useAppSelector } from 'utilities/hooks';
import { isAtLeastSuperAdminFromState } from 'utilities/roles-util';
import { DateFormatterModes, getDateToString } from 'view/shared/components/date-to-string';
import { ConfirmDialog } from 'view/shared/components/dialogs/bi-dialog-confirm/dialog-confirm';
import { Spinner } from 'view/components/spinner/spinner';
import { ROW_HEIGHT } from './bi-table';
import './service-history-table.scss';

export interface Props {
	isLoading?: boolean;
	data: IUnitServiceHistoryDto[];
	unitId?: number;
}
export const ServiceHistoryTable: FC<Props> = memo<Props>(({ isLoading, data, unitId }) => {
	const sorting = useAppSelector(state => state.serviceHistoryTableSettingsReducer.sorting);
	const allColumnOrder = useAppSelector(state => state.serviceHistoryTableSettingsReducer.columnOrder);
	const allColumnWidths = useAppSelector(state => state.serviceHistoryTableSettingsReducer.columnWidths);
	const allColumns = useAppSelector(state => state.serviceHistoryTableSettingsReducer.columns);
	const isSuperAdmin = useAppSelector(state => isAtLeastSuperAdminFromState(state));
	const columns = isSuperAdmin ? allColumns : allColumns.filter(col => col.name !== ServiceHistoryColumnNames.delete);
	const columnOrder = isSuperAdmin
		? allColumnOrder
		: allColumnOrder.filter(col => col !== ServiceHistoryColumnNames.delete);
	const columnWidths = isSuperAdmin
		? allColumnWidths
		: allColumnWidths.filter(col => col.columnName !== ServiceHistoryColumnNames.delete);
	const fixedRightColumns = isSuperAdmin ? ['download', 'delete'] : ['download'];
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [fileToDelete, setFileToDelete] = useState<string | null>(null);

	const dispatch = useAppDispatch();

	const changeColumnWidths = useCallback(
		(columnWidths: TableColumnWidthInfo[]) => {
			setSize(columnWidths as TableColumnWidthInfoGeneric<ServiceHistoryDtoKeys>[])(dispatch);
		},
		[dispatch]
	);

	const changeColumnOrder = useCallback(
		(newOrder: string[]) => {
			setOrder(newOrder as ServiceHistoryDtoKeys[])(dispatch);
		},
		[dispatch]
	);

	const changeSorting = useCallback(
		(sorting: Sorting[]) => {
			setSorting(sorting as SortingGeneric<ServiceHistoryDtoKeys>[])(dispatch);
		},
		[dispatch]
	);

	const onOpenDocument = useCallback(async (unitId: number, fileName: string) => {
		const file = await unitServiceHistoryClient.downloadServiceDocument(unitId, fileName);
		if (file?.data) {
			const fileURL = URL.createObjectURL(file.data);
			window.open(fileURL, '_blank');
		}
	}, []);

	const onDeleteDocument = useCallback(
		(fileName: string) => {
			if (!unitId) return;
			setFileToDelete(fileName);
			setShowDeleteDialog(true);
		},
		[unitId]
	);

	const handleConfirmDelete = useCallback(async () => {
		if (!unitId || !fileToDelete) return;

		await unitServiceHistoryClient.deleteServiceDocument(unitId, fileToDelete);
		// Refresh the data after successful deletion
		getUnitServiceHistories(unitId)(dispatch);
		setShowDeleteDialog(false);
		setFileToDelete(null);
	}, [dispatch, unitId, fileToDelete]);

	const handleCancelDelete = useCallback(() => {
		setShowDeleteDialog(false);
		setFileToDelete(null);
	}, []);

	const DownloadFormatter = ({ row }: DataTypeProvider.ValueFormatterProps) => {
		const serviceHistoryRow = row as IUnitServiceHistoryDto;
		if (!serviceHistoryRow.fileName) return null;
		return (
			<Button
				className="p-button-background-color-org-grey"
				label={localized('Open')}
				onClick={() => onOpenDocument(serviceHistoryRow.unitId, serviceHistoryRow.fileName!)}
			/>
		);
	};

	const DeleteFormatter = ({ row }: DataTypeProvider.ValueFormatterProps) => {
		const serviceHistoryRow = row as IUnitServiceHistoryDto;
		if (!serviceHistoryRow.fileName) return null;
		return (
			<Button
				className="p-button-danger"
				label={localized('Delete')}
				onClick={() => onDeleteDocument(serviceHistoryRow.fileName!)}
			/>
		);
	};

	const ServiceTypeFormatter = ({ value }: DataTypeProvider.ValueFormatterProps) => {
		const typeName = ServiceDocumentType[value as ServiceDocumentType];
		return <span>{localized(typeName as any)}</span>;
	};

	const GenericTypeProvider = (props: DataTypeProviderProps) => <DataTypeProvider {...props} />;

	const getRowId = (row: IUnitServiceHistoryDto) => {
		return Guid.create().toString();
	};

	const DateFormatter = (obj: { value: Date }) => {
		return <span>{getDateToString(obj.value, DateFormatterModes.withMinutes)}</span>;
	};

	const DateOnlyFormatter = (obj: { value: Date }) => {
		return <span>{getDateToString(obj.value, DateFormatterModes.normal)}</span>;
	};

	const TableComponent = ({ ...restProps }) => (
		<VirtualTable.Table {...restProps} className="bi-report-table-striped" />
	);

	const HeaderComponent = ({ ...restProps }) => <VirtualTable.TableHead {...restProps} className="table-header" />;

	const TableRow = (obj: any) => <VirtualTable.Row {...obj} />;

	const Root = (props: Grid.RootProps) => <Grid.Root {...props} className="service-history-table" />;

	const noCell = (rest: Table.NoDataCellProps) => ServiceHistoryNoDataComponent(rest);

	const ServiceHistoryNoDataComponent = (restProps: Table.NoDataCellProps): JSX.Element => {
		if (isLoading) {
			return (
				<td colSpan={restProps.colSpan} className="py-5">
					<div className="bi-center-across-columns padding-vertical-10px">
						<Spinner shouldOverlay={false} shouldUseAbsolutePositioning={true} />
					</div>
				</td>
			);
		}

		return <VirtualTable.NoDataCell {...restProps} getMessage={() => localized('NoData')} />;
	};

	const columnState = {
		dateColumns: [ServiceHistoryColumnNames.createdOn],
		dateOnlyColumns: [ServiceHistoryColumnNames.dateOfService],
		downloadColumns: [ServiceHistoryColumnNames.download],
		deleteColumns: isSuperAdmin ? [ServiceHistoryColumnNames.delete] : [],
		serviceTypeColumns: [ServiceHistoryColumnNames.serviceDocumentType],
	};

	return (
		<>
			<Grid rootComponent={Root} rows={data || []} columns={columns} getRowId={getRowId}>
				<GenericTypeProvider for={columnState.dateColumns} formatterComponent={DateFormatter} />
				<GenericTypeProvider for={columnState.dateOnlyColumns} formatterComponent={DateOnlyFormatter} />
				<GenericTypeProvider for={columnState.downloadColumns} formatterComponent={DownloadFormatter} />
				<GenericTypeProvider for={columnState.deleteColumns} formatterComponent={DeleteFormatter} />
				<GenericTypeProvider for={columnState.serviceTypeColumns} formatterComponent={ServiceTypeFormatter} />

				<DragDropProvider />
				<SortingState sorting={sorting} onSortingChange={changeSorting} />
				<IntegratedSorting />

				<VirtualTable
					tableComponent={TableComponent}
					headComponent={HeaderComponent}
					height="auto"
					noDataCellComponent={noCell}
					rowComponent={TableRow}
					estimatedRowHeight={ROW_HEIGHT}
				/>

				<TableColumnReordering onOrderChange={changeColumnOrder} order={columnOrder} />
				<TableColumnResizing onColumnWidthsChange={changeColumnWidths} columnWidths={columnWidths} />
				<TableHeaderRow showSortingControls={true} />
				<TableFixedColumns rightColumns={fixedRightColumns} />
			</Grid>

			<ConfirmDialog
				visible={showDeleteDialog}
				onHide={handleCancelDelete}
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
				header={localized('Delete')}
				dialogMessage={localized('AreYouSureYouWantToDeleteThisDocument')}
				confirmColor="org-red"
				confirmMessage={localized('Delete')}
				cancelMessage={localized('Cancel')}
			/>
		</>
	);
});
